import {
    DEFAULT_AIRCRAFT_MODEL,
    DEFAULT_BATTERY_TAB,
    DEFAULT_PHASE,
    DEFAULT_VIEW,
    FILTER_KEYS,
    MOCK_CURRENT_USER,
    ORDER_SUCCESS_DURATION_MS,
    PART_PRICE,
    PHASE_HEALTH_FAULTS,
    PHASE_USAGE
} from '../../constants';
import { applyAirworthy } from '../../helpers/applyAirworthy';
import { applyBatteryStatus } from '../../helpers/applyBatteryStatus';
import { applyLifecycle } from '../../helpers/applyLifecycle';
import { applyServiceRecords } from '../../helpers/applyServiceRecords';
import { computeRecommendations } from '../../helpers/computeRecommendations';
import { generatePoNumber } from '../../helpers/generatePoNumber';
import { getCanEditRecord } from '../../helpers/getCanEditRecord';
import { getModelIntervals } from '../../helpers/getModelIntervals';
import { isDueSoon } from '../../helpers/isDueSoon';
import { BATTERIES, DEFAULT_SERVICE_INTERVALS, DRONES } from '../../mockData';
import type {
    AircraftModelId,
    AirworthyLog,
    BatteryLifecycleStatus,
    BatteryStatusLog,
    CartLine,
    DroneNameMap,
    DroneReadiness,
    FilterKey,
    LifecycleLog,
    LogService,
    MaintenanceView,
    ModelServiceIntervalMap,
    OnOpenEditService,
    OnSaveEdit,
    PartId,
    Recommendation,
    ServiceIntervalMap,
    SetAirworthy,
    SetBatteryStatus,
    SetLifecycle,
    SettableLifecycleStatus,
    TabKey,
    UpdateService,
    UseDataArgs,
    UseDataReturn,
    VoidService
} from '../../types';
import useServiceRecords from '../useServiceRecords';
import { useMemo, useState } from 'react';

import useIsAdmin from 'hooks/useIsAdmin';

const useData = ({ phase = DEFAULT_PHASE }: UseDataArgs): UseDataReturn => {
    const { isAdmin } = useIsAdmin();
    const [activeView, setActiveView] = useState<MaintenanceView>(DEFAULT_VIEW);
    const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [drawerDroneId, setDrawerDroneId] = useState<string | null>(null);
    const [logServiceDroneId, setLogServiceDroneId] = useState<string | null>(null);
    // Target of the edit-service panel; the record is re-resolved from serviceRecords
    // and gated by getCanEditRecord on every render, so a record voided (or gone)
    // mid-edit resolves to null and the panel closes via the derived isEditServiceOpen.
    const [editServiceTarget, setEditServiceTarget] = useState<
        Parameters<OnOpenEditService>[0] | null
    >(null);
    const [activeTab, setActiveTab] = useState<TabKey>('health');
    const [cart, setCart] = useState<CartLine[]>([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [orderPoNumber, setOrderPoNumber] = useState<string | null>(null);
    // Per-model editable intervals. Each drone's rings/Due resolve against its own
    // model's map (via getDroneIntervals downstream). The config panel edits one model
    // at a time, chosen via the model selector (configModel).
    const [serviceIntervals, setServiceIntervals] =
        useState<ModelServiceIntervalMap>(DEFAULT_SERVICE_INTERVALS);
    const [configModel, setConfigModel] = useState<AircraftModelId>(DEFAULT_AIRCRAFT_MODEL);
    const [isIntervalConfigOpen, setIsIntervalConfigOpen] = useState(false);
    // Manual airworthy overrides layered on top of each drone's auto-health readiness.
    // The effective readiness (override ?? drone.readiness) drives the badge and the
    // GROUNDED filter; a matching log records who set it and when.
    const [airworthyOverrides, setAirworthyOverrides] = useState<Record<string, DroneReadiness>>(
        {}
    );
    const [airworthyLogs, setAirworthyLogs] = useState<Record<string, AirworthyLog>>({});
    // Manual claim/release overrides layered on each drone's lifecycle axis. Only the
    // settable states ('claimed'/'released') are ever written here; 'enabled'/'disabled'
    // remain data-only. A matching log records who set it and when.
    const [lifecycleOverrides, setLifecycleOverrides] = useState<
        Record<string, SettableLifecycleStatus>
    >({});
    const [lifecycleLogs, setLifecycleLogs] = useState<Record<string, LifecycleLog>>({});
    const [drawerBatteryId, setDrawerBatteryId] = useState<string | null>(null);
    const [activeBatteryTab, setActiveBatteryTab] = useState<string>(DEFAULT_BATTERY_TAB);
    // Manual lifecycle overrides layered on top of each pack. Unlike drones, this writes a
    // separate axis (lifecycleStatus) and never overwrites the auto health `status`. A
    // matching log records who set it and when.
    const [batteryStatusOverrides, setBatteryStatusOverrides] = useState<
        Record<string, BatteryLifecycleStatus>
    >({});
    const [batteryStatusLogs, setBatteryStatusLogs] = useState<Record<string, BatteryStatusLog>>(
        {}
    );
    const {
        logService: appendServiceRecord,
        serviceRecords,
        updateService: updateServiceRecord,
        voidService: voidServiceRecord
    } = useServiceRecords();

    const configServiceIntervals = getModelIntervals({
        model: configModel,
        serviceIntervals
    });

    // Pooled batteries aren't tied to one airframe, so service life is measured against
    // the configured battery cycle interval of the model currently selected in the config.
    const batteryCycleInterval = configServiceIntervals.battery;

    // Service-record resets and calibration overlays apply to the raw fleet first so
    // recommendations and the visible fleet both read the same adjusted counters — a
    // logged replacement must clear its recommendation, not just its ring.
    const serviceAdjustedDrones = useMemo(
        () =>
            DRONES.map(drone =>
                applyServiceRecords({ drone, records: serviceRecords[drone.id] ?? [] })
            ),
        [serviceRecords]
    );

    const recommendations = useMemo(
        () => computeRecommendations({ drones: serviceAdjustedDrones, serviceIntervals }),
        [serviceAdjustedDrones, serviceIntervals]
    );

    const visibleFilterKeys = useMemo<FilterKey[]>(() => {
        const minPhaseByFilter: Partial<Record<FilterKey, number>> = {
            caution: PHASE_HEALTH_FAULTS,
            critical: PHASE_HEALTH_FAULTS,
            dueSoon: PHASE_USAGE
        };

        return FILTER_KEYS.filter(key => phase >= (minPhaseByFilter[key] ?? 0));
    }, [phase]);

    const droneNames = useMemo<DroneNameMap>(() => {
        const map: DroneNameMap = {};

        DRONES.forEach(drone => {
            map[drone.id] = drone.name;
        });

        return map;
    }, []);

    const cartItemCount = cart.reduce((sum, line) => sum + line.qty, 0);
    const cartSubtotal = cart.reduce((sum, line) => sum + PART_PRICE[line.partId] * line.qty, 0);

    const effectiveDrones = useMemo(
        () =>
            serviceAdjustedDrones.map(drone =>
                applyLifecycle({
                    drone: applyAirworthy({ airworthyLogs, airworthyOverrides, drone }),
                    lifecycleLogs,
                    lifecycleOverrides
                })
            ),
        [
            airworthyLogs,
            airworthyOverrides,
            lifecycleLogs,
            lifecycleOverrides,
            serviceAdjustedDrones
        ]
    );

    const visibleDrones = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return effectiveDrones.filter(drone => {
            if (activeFilter === 'critical' && drone.status !== 'critical') {
                return false;
            }

            if (activeFilter === 'caution' && drone.status !== 'caution') {
                return false;
            }

            if (activeFilter === 'grounded' && drone.readiness !== 'grounded') {
                return false;
            }

            if (activeFilter === 'dueSoon' && !isDueSoon({ drone, serviceIntervals })) {
                return false;
            }

            if (query && !`${drone.name} ${drone.serial}`.toLowerCase().includes(query)) {
                return false;
            }

            return true;
        });
    }, [activeFilter, effectiveDrones, searchQuery, serviceIntervals]);

    const drawerDrone = drawerDroneId
        ? (effectiveDrones.find(drone => drone.id === drawerDroneId) ?? null)
        : null;

    const logServiceDrone = logServiceDroneId
        ? (effectiveDrones.find(drone => drone.id === logServiceDroneId) ?? null)
        : null;

    const resolvedEditServiceRecord = editServiceTarget
        ? ((serviceRecords[editServiceTarget.droneId] ?? []).find(
              record => record.id === editServiceTarget.recordId
          ) ?? null)
        : null;

    // The permission rule is folded into the resolution: a record the current user
    // can no longer edit (voided mid-edit, for instance) resolves to null, which
    // closes the panel via the derived isEditServiceOpen.
    const editServiceRecord =
        resolvedEditServiceRecord &&
        getCanEditRecord({ isAdmin, record: resolvedEditServiceRecord })
            ? resolvedEditServiceRecord
            : null;

    const effectiveBatteries = useMemo(
        () =>
            BATTERIES.map(battery =>
                applyBatteryStatus({ battery, batteryStatusLogs, batteryStatusOverrides })
            ),
        [batteryStatusLogs, batteryStatusOverrides]
    );

    const drawerBattery =
        effectiveBatteries.find(battery => battery.id === drawerBatteryId) ?? null;

    const handleAddToCart = (recommendation: Recommendation) =>
        setCart(previousCart => {
            const existing = previousCart.find(line => line.partId === recommendation.partId);

            if (!existing) {
                return [
                    ...previousCart,
                    {
                        droneIds: [...recommendation.droneIds],
                        partId: recommendation.partId,
                        qty: recommendation.qty
                    }
                ];
            }

            return previousCart.map(line =>
                line.partId === recommendation.partId
                    ? {
                          ...line,
                          droneIds: Array.from(
                              new Set([...line.droneIds, ...recommendation.droneIds])
                          ),
                          qty: line.qty + recommendation.qty
                      }
                    : line
            );
        });

    const handleIncrementCartLine = (partId: PartId) =>
        setCart(previousCart =>
            previousCart.map(line =>
                line.partId === partId ? { ...line, qty: line.qty + 1 } : line
            )
        );

    const handleDecrementCartLine = (partId: PartId) =>
        setCart(previousCart =>
            previousCart
                .map(line => (line.partId === partId ? { ...line, qty: line.qty - 1 } : line))
                .filter(line => line.qty > 0)
        );

    const handleRemoveCartLine = (partId: PartId) =>
        setCart(previousCart => previousCart.filter(line => line.partId !== partId));

    const handleOpenCart = () => setIsCartOpen(true);

    const handleCloseCart = () => setIsCartOpen(false);

    const handlePlaceOrder = () => {
        setOrderPoNumber(generatePoNumber());

        window.setTimeout(() => {
            setOrderPoNumber(null);
            setCart([]);
            setIsCartOpen(false);
        }, ORDER_SUCCESS_DURATION_MS);
    };

    const setAirworthy: SetAirworthy = ({ droneId, status }) => {
        const log: AirworthyLog = {
            at: new Date().toISOString(),
            by: MOCK_CURRENT_USER,
            status
        };

        setAirworthyOverrides(previous => ({ ...previous, [droneId]: status }));
        setAirworthyLogs(previous => ({ ...previous, [droneId]: log }));
    };

    const setLifecycle: SetLifecycle = ({ droneId, status }) => {
        const log: LifecycleLog = {
            at: new Date().toISOString(),
            by: MOCK_CURRENT_USER,
            status
        };

        setLifecycleOverrides(previous => ({ ...previous, [droneId]: status }));
        setLifecycleLogs(previous => ({ ...previous, [droneId]: log }));
    };

    const setBatteryStatus: SetBatteryStatus = ({ batteryId, status }) => {
        const log: BatteryStatusLog = {
            at: new Date().toISOString(),
            by: MOCK_CURRENT_USER,
            status
        };

        setBatteryStatusOverrides(previous => ({ ...previous, [batteryId]: status }));
        setBatteryStatusLogs(previous => ({ ...previous, [batteryId]: log }));
    };

    const handleOpenDrawer = (droneId: string) => {
        setDrawerDroneId(droneId);
        setActiveTab('health');
    };

    const handleCloseDrawer = () => setDrawerDroneId(null);

    const handleOpenLogService = (droneId: string) => setLogServiceDroneId(droneId);

    const handleCloseLogService = () => setLogServiceDroneId(null);

    const handleOpenEditService: OnOpenEditService = ({ droneId, recordId }) =>
        setEditServiceTarget({ droneId, recordId });

    const handleCloseEditService = () => setEditServiceTarget(null);

    // Save flow: append the stamped record, then close the modal on the next line —
    // no callbacks held in state.
    const logService: LogService = ({ draft, droneId }) => {
        appendServiceRecord({ draft, droneId });
        setLogServiceDroneId(null);
    };

    // Guarded here, not just hidden in the UI: editing is limited to admins and the
    // record's own author, and voided records are frozen (getCanEditRecord is the one
    // rule the guard and the edit affordance share). Save then closes the panel on
    // the next line — no callbacks held in state.
    const handleUpdateService: UpdateService = ({ droneId, edit, recordId }) => {
        const record = (serviceRecords[droneId] ?? []).find(candidate => candidate.id === recordId);

        if (!record || !getCanEditRecord({ isAdmin, record })) {
            setEditServiceTarget(null);

            return;
        }

        updateServiceRecord({ droneId, edit, recordId });
        setEditServiceTarget(null);
    };

    // Thin save wrapper for the edit panel: the panel knows only the edit; the
    // stored target supplies droneId/recordId.
    const handleSaveEditService: OnSaveEdit = ({ edit }) => {
        if (!editServiceTarget) {
            return;
        }

        handleUpdateService({ ...editServiceTarget, edit });
    };

    // Guarded here, not just hidden in the UI: voiding is an admin-only mutation.
    const voidService: VoidService = ({ droneId, recordId }) => {
        if (!isAdmin) {
            return;
        }

        voidServiceRecord({ droneId, recordId });
    };

    const handleOpenBatteryDrawer = (batteryId: string) => {
        setDrawerBatteryId(batteryId);
        setActiveBatteryTab(DEFAULT_BATTERY_TAB);
    };

    const handleCloseBatteryDrawer = () => setDrawerBatteryId(null);

    const handleBatteryTabChange = (tab: string) => setActiveBatteryTab(tab);

    const handleOpenIntervalConfig = () => setIsIntervalConfigOpen(true);

    const handleCloseIntervalConfig = () => setIsIntervalConfigOpen(false);

    const handleSaveIntervals = ({ intervals }: { intervals: ServiceIntervalMap }) => {
        setServiceIntervals(previous => ({ ...previous, [configModel]: intervals }));
        setIsIntervalConfigOpen(false);
    };

    return {
        activeBatteryTab,
        activeFilter,
        activeTab,
        activeView,
        batteries: effectiveBatteries,
        batteryCycleInterval,
        batteryStatusLogs,
        cart,
        cartItemCount,
        cartSubtotal,
        configModel,
        configServiceIntervals,
        drawerBattery,
        drawerDrone,
        droneNames,
        editServiceRecord,
        handleAddToCart,
        handleBatteryTabChange,
        handleCloseBatteryDrawer,
        handleCloseCart,
        handleCloseDrawer,
        handleCloseEditService,
        handleCloseIntervalConfig,
        handleCloseLogService,
        handleDecrementCartLine,
        handleIncrementCartLine,
        handleOpenBatteryDrawer,
        handleOpenCart,
        handleOpenDrawer,
        handleOpenEditService,
        handleOpenIntervalConfig,
        handleOpenLogService,
        handlePlaceOrder,
        handleRemoveCartLine,
        handleSaveEditService,
        handleSaveIntervals,
        isAdmin,
        isBatteryDrawerOpen: drawerBattery !== null,
        isCartOpen,
        isDrawerOpen: drawerDrone !== null,
        isEditServiceOpen: editServiceRecord !== null,
        isIntervalConfigOpen,
        isLogServiceOpen: logServiceDrone !== null,
        logService,
        logServiceDrone,
        orderPoNumber,
        recommendations,
        searchQuery,
        serviceIntervals,
        serviceRecords,
        setActiveFilter,
        setActiveTab,
        setActiveView,
        setAirworthy,
        setBatteryStatus,
        setConfigModel,
        setLifecycle,
        setSearchQuery,
        visibleDrones,
        visibleFilterKeys,
        voidService
    };
};

export default useData;
